import React, { useState, useEffect } from 'react';
import { icons, getIconById } from '../../utils/iconUtils';
import * as HeroIcons from '@heroicons/react/24/outline';

interface IconSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

// Extract categories from the comments in the icon array
const getCategories = () => {
  const categories: string[] = [];
  let currentCategory = '';
  
  // Scan the icon array source to extract categories from comments
  const iconArraySource = icons.toString();
  const lines = iconArraySource.split(',');
  
  for (const line of lines) {
    if (line.includes('// ')) {
      const match = line.match(/\/\/\s*([^/]+)/);
      if (match && match[1]) {
        currentCategory = match[1].trim();
        if (!categories.includes(currentCategory)) {
          categories.push(currentCategory);
        }
      }
    }
  }
  
  // Hardcode the categories since the runtime approach isn't reliable
  return [
    'Basic',
    'Content',
    'Communication',
    'Time',
    'People',
    'Business',
    'Transportation',
    'Home & Construction',
    'Nature & Environment',
    'Technology',
    'Action & Interface',
    'Miscellaneous'
  ];
};

// Group icons by their position relative to category comments
const groupIconsByCategory = () => {
  const grouped: Record<string, typeof icons> = {};
  const categories = getCategories();
  
  let currentCategory = 'Basic'; // default
  
  for (const icon of icons) {
    // Check if we have a category match at the beginning of the icon name
    const matchingCategory = categories.find(cat => 
      icon.id.startsWith(cat.toLowerCase().replace(/\s+/g, '-'))
    );
    
    if (matchingCategory) {
      currentCategory = matchingCategory;
    }
    
    if (!grouped[currentCategory]) {
      grouped[currentCategory] = [];
    }
    
    grouped[currentCategory].push(icon);
  }
  
  return { categories, grouped };
};

const { categories } = groupIconsByCategory();

// Dynamic icon rendering based on string name
const DynamicIcon = ({ iconName, className }: { iconName: string, className?: string }) => {
  try {
    // @ts-ignore - We're dynamically accessing properties
    const IconComponent = HeroIcons[iconName.split('-').map(part => 
      part.charAt(0).toUpperCase() + part.slice(1)).join('') + 'Icon'];

    if (!IconComponent) {
      return <HeroIcons.StarIcon className={className} />;
    }

    return <IconComponent className={className} />;
  } catch (error) {
    // Fallback to star icon if the icon doesn't exist
    return <HeroIcons.StarIcon className={className} />;
  }
};

const IconSelector: React.FC<IconSelectorProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const selectedIcon = getIconById(value);

  // Function to check if an icon matches the search text
  const matchesSearch = (icon: typeof icons[0]) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return icon.name.toLowerCase().includes(searchLower) || 
           icon.id.toLowerCase().includes(searchLower);
  };
  
  // Filter icons based on search and category
  const filteredIcons = icons.filter(icon => {
    return matchesSearch(icon);
  });

  // Determine which icons to show based on the active category
  const getIconsForCategory = () => {
    if (!activeCategory) return icons;
    
    // Find the index of the category in the array
    const categoryIndex = categories.indexOf(activeCategory);
    if (categoryIndex === -1) return icons;
    
    // Find the next category's index
    const nextCategoryIndex = categoryIndex + 1;
    const nextCategory = nextCategoryIndex < categories.length ? categories[nextCategoryIndex] : null;
    
    // Get the start index (where comment for the category appears)
    const startComment = `// ${activeCategory}`;
    const iconArrayStr = JSON.stringify(icons);
    const startIndex = iconArrayStr.indexOf(startComment);
    
    if (startIndex === -1) return icons;
    
    // If there's a next category, find its index, otherwise use the end of the array
    let endIndex = iconArrayStr.length;
    if (nextCategory) {
      const nextComment = `// ${nextCategory}`;
      const nextIndex = iconArrayStr.indexOf(nextComment);
      if (nextIndex !== -1) {
        endIndex = nextIndex;
      }
    }
    
    // Extract icons between start and end index
    // Since this approach isn't perfect, fallback to filtering by icon properties
    return icons.filter(icon => {
      // Match categories based on icon properties
      if (activeCategory === 'Basic') {
        return ['star', 'check', 'heart', 'shield-check', 'home', 'wrench', 'trophy'].includes(icon.id);
      }
      else if (activeCategory === 'Content') {
        return icon.id.includes('document') || icon.id.includes('book') || icon.id === 'newspaper' || icon.id === 'photo';
      }
      else if (activeCategory === 'Communication') {
        return icon.id.includes('chat') || icon.id.includes('envelope') || icon.id === 'phone' || icon.id === 'megaphone' || icon.id === 'bell' || icon.id === 'speaker-wave';
      }
      else if (activeCategory === 'Time') {
        return icon.id.includes('clock') || icon.id.includes('calendar') || icon.id === 'hourglass';
      }
      else if (activeCategory === 'People') {
        return icon.id.includes('user') || icon.id === 'identification';
      }
      else if (activeCategory === 'Business') {
        return icon.id === 'briefcase' || icon.id.includes('currency') || icon.id.includes('chart') || icon.id.includes('building') || icon.id === 'credit-card' || icon.id === 'banknotes' || icon.id === 'receipt-percent';
      }
      else if (activeCategory === 'Transportation') {
        return icon.id.includes('truck') || icon.id === 'car' || icon.id === 'map' || icon.id === 'map-pin';
      }
      else if (activeCategory === 'Home & Construction') {
        return icon.id === 'key' || icon.id === 'home-modern' || icon.id === 'paint-brush' || icon.id === 'scissors' || icon.id === 'squares-2x2' || icon.id === 'ruler' || icon.id === 'wrench-screwdriver' || icon.id === 'beaker';
      }
      else if (activeCategory === 'Nature & Environment') {
        return icon.id === 'sparkles' || icon.id === 'fire' || icon.id === 'bolt' || icon.id === 'sun' || icon.id === 'cloud' || icon.id === 'hand-raised' || icon.id.includes('globe');
      }
      else if (activeCategory === 'Technology') {
        return icon.id === 'computer-desktop' || icon.id === 'device-phone-mobile' || icon.id.includes('cog') || icon.id === 'command-line' || icon.id === 'cpu-chip' || icon.id === 'wifi' || icon.id === 'signal' || icon.id === 'lock-closed' || icon.id === 'server';
      }
      else if (activeCategory === 'Action & Interface') {
        return icon.id.includes('arrow') || icon.id === 'eye' || icon.id === 'hand-thumb-up' || icon.id === 'heart-filled' || icon.id === 'link' || icon.id === 'magnifying-glass' || icon.id === 'paper-airplane' || icon.id === 'rss' || icon.id === 'gift';
      }
      else if (activeCategory === 'Miscellaneous') {
        return icon.id === 'academic-cap' || icon.id === 'adjustments-horizontal' || icon.id === 'archive-box' || icon.id === 'puzzle-piece' || icon.id === 'light-bulb' || icon.id === 'tag' || icon.id === 'ticket' || icon.id.includes('shopping') || icon.id === 'camera';
      }
      
      return false;
    });
  };

  // Close the dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.icon-selector-dropdown')) {
        setIsOpen(false);
        setSearchText('');
        setActiveCategory(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Icon
      </label>
      
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-full flex items-center justify-between bg-white border border-gray-300 rounded-md py-2 px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
        >
          <div className="flex items-center">
            <div className="mr-2 h-6 w-6 flex-shrink-0 rounded-full bg-accent-50 p-1">
              <DynamicIcon iconName={selectedIcon.id} className="h-full w-full text-accent-600" />
            </div>
            <span className="text-gray-700">{selectedIcon.name}</span>
          </div>
          <HeroIcons.ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg icon-selector-dropdown">
          <div className="p-2 border-b">
            <div className="relative">
              <HeroIcons.MagnifyingGlassIcon className="pointer-events-none absolute top-2 left-3 h-4 w-4 text-gray-400" aria-hidden="true" />
              <input
                type="text"
                className="w-full py-1.5 pl-10 pr-3 text-sm leading-5 text-gray-900 placeholder-gray-400 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
                placeholder="Search icons..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto p-2">
            {searchText ? (
              // Show search results
              <div className="grid grid-cols-5 gap-1">
                {filteredIcons.length > 0 ? filteredIcons.map((icon) => (
                  <button
                    key={icon.id}
                    type="button"
                    onClick={() => {
                      onChange(icon.id);
                      setIsOpen(false);
                      setSearchText('');
                      setActiveCategory(null);
                    }}
                    className={`flex flex-col items-center justify-center p-1.5 rounded transition-colors ${
                      value === icon.id
                        ? 'bg-accent-100 text-accent-700 ring-1 ring-accent-200'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className={`h-8 w-8 p-1.5 rounded-full mb-1 ${
                      value === icon.id ? 'bg-accent-50' : 'bg-gray-50'
                    }`}>
                      <DynamicIcon iconName={icon.id} className={`h-full w-full ${
                        value === icon.id ? 'text-accent-600' : 'text-gray-500'
                      }`} />
                    </div>
                    <span className="text-xs font-medium truncate w-full text-center">
                      {icon.name}
                    </span>
                  </button>
                )) : (
                  <div className="col-span-5 py-4 text-center text-sm text-gray-500">
                    No icons found matching "{searchText}"
                  </div>
                )}
              </div>
            ) : (
              // Show categorized icons 
              <div className="space-y-4">
                {/* Category Tabs */}
                <div className="flex flex-wrap -mx-1 mb-2">
                  {categories.map(category => (
                    <button
                      key={category}
                      type="button"
                      className={`text-xs px-2 py-1 m-1 rounded ${
                        activeCategory === category 
                          ? 'bg-accent-100 text-accent-700' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => setActiveCategory(
                        activeCategory === category ? null : category
                      )}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                {/* Icon Grid */}
                <div className="grid grid-cols-5 gap-1">
                  {(activeCategory ? getIconsForCategory() : icons).map((icon) => (
                    <button
                      key={icon.id}
                      type="button"
                      onClick={() => {
                        onChange(icon.id);
                        setIsOpen(false);
                        setSearchText('');
                        setActiveCategory(null);
                      }}
                      className={`flex flex-col items-center justify-center p-1.5 rounded transition-colors ${
                        value === icon.id
                          ? 'bg-accent-100 text-accent-700 ring-1 ring-accent-200'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <div className={`h-8 w-8 p-1.5 rounded-full mb-1 ${
                        value === icon.id ? 'bg-accent-50' : 'bg-gray-50'
                      }`}>
                        <DynamicIcon iconName={icon.id} className={`h-full w-full ${
                          value === icon.id ? 'text-accent-600' : 'text-gray-500'
                        }`} />
                      </div>
                      <span className="text-xs font-medium truncate w-full text-center">
                        {icon.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="p-2 border-t bg-gray-50">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setSearchText('');
                setActiveCategory(null);
              }}
              className="w-full px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IconSelector; 